import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { ImageManager } from '@/lib/imageManager';
import {
  ImagePlus,
  Trash2,
  Wand2,
  Upload,
  Loader2,
  Eye,
  Download,
  GripVertical
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface ProductImageGalleryProps {
  images: string[];
  onImagesChange: (images: string[]) => void;
  productTitle?: string;
}

// Sortable Image Item Component
const SortableImageItem = ({
  imageUrl,
  index,
  onRemove,
  onPreview,
  onDownload
}: {
  imageUrl: string;
  index: number;
  onRemove: () => void;
  onPreview: () => void;
  onDownload: () => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: imageUrl });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`relative group cursor-move ${isDragging ? 'shadow-lg' : ''}`}
    >
      <CardContent className="p-2">
        <div className="relative aspect-square">
          <img
            src={ImageManager.getImageUrl(imageUrl)}
            alt={`תמונת מוצר ${index + 1}`}
            className="w-full h-full object-cover rounded-md"
            onError={(e) => ImageManager.handleImageError(e)}
          />

          {/* Main image indicator for first image */}
          {index === 0 && (
            <Badge className="absolute top-2 right-2 bg-primary">
              תמונה ראשית
            </Badge>
          )}

          {/* Drag handle - always visible and larger for better usability */}
          <div
            className="absolute top-2 left-2 bg-black/70 text-white p-2 rounded cursor-move transition-all hover:bg-black/90"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="w-5 h-5" />
          </div>
        </div>

        {/* Action buttons below image */}
        <div className="flex justify-center gap-1 mt-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onPreview}
            title="תצוגה מקדימה"
            className="p-2"
          >
            <Eye className="w-4 h-4" />
          </Button>

          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onDownload}
            title="הורד תמונה"
            className="p-2"
          >
            <Download className="w-4 h-4" />
          </Button>

          <Button
            type="button"
            size="sm"
            variant="destructive"
            onClick={onRemove}
            title="הסר תמונה"
            className="p-2"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const ProductImageGallery = ({
  images,
  onImagesChange,
  productTitle = ''
}: ProductImageGalleryProps) => {
  const [isAiDialogOpen, setIsAiDialogOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiStyle, setAiStyle] = useState('professional');
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = images.findIndex(img => img === active.id);
      const newIndex = images.findIndex(img => img === over?.id);

      const newImages = arrayMove(images, oldIndex, newIndex);
      onImagesChange(newImages);
      toast.success('סדר התמונות עודכן');
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    const uploadingToast = toast.loading(`מעלה ${fileArray.length} תמונות...`);

    try {
      // Upload all files in parallel
      const uploadPromises = fileArray.map(file => ImageManager.uploadImage(file));
      const results = await Promise.all(uploadPromises);

      // Filter successful uploads
      const successfulUrls = results
        .filter(result => result.success && result.url)
        .map(result => result.url!);

      const failedCount = results.length - successfulUrls.length;

      if (successfulUrls.length > 0) {
        // Add all uploaded image URLs to the gallery
        const updatedImages = [...images, ...successfulUrls];
        onImagesChange(updatedImages);

        toast.success(
          `${successfulUrls.length} תמונות הועלו בהצלחה${failedCount > 0 ? ` (${failedCount} נכשלו)` : ''}`,
          { id: uploadingToast }
        );
      } else {
        toast.error('כל העלאות התמונות נכשלו', { id: uploadingToast });
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('שגיאה בהעלאת התמונות', { id: uploadingToast });
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = async (index: number) => {
    console.log('Removing image at index:', index, 'Current images:', images);

    if (index < 0 || index >= images.length) {
      console.error('Invalid index:', index);
      return;
    }

    const imageToRemove = images[index];
    console.log('Image to remove:', imageToRemove);

    // Create the updated images array first
    const updatedImages = [...images];
    updatedImages.splice(index, 1);
    console.log('Updated images array:', updatedImages);

    // Delete from storage if it's a Supabase URL (don't wait for this)
    if (imageToRemove && !imageToRemove.startsWith('data:')) {
      console.log('Deleting from storage:', imageToRemove);
      ImageManager.deleteImage(imageToRemove).then(deleted => {
        console.log('Deletion result:', deleted);
      }).catch(error => {
        console.error('Error deleting from storage:', error);
      });
    }

    // Update state
    onImagesChange(updatedImages);
    toast.success('תמונה הוסרה בהצלחה');
  };

  const handleGenerateAI = async () => {
    if (!aiPrompt.trim() && !productTitle.trim()) {
      toast.error('אנא הזן הנחיה או וודא שיש כותרת למוצר');
      return;
    }

    setIsGenerating(true);
    try {
      console.log('Calling AI image generation function...');

      const { data, error } = await supabase.functions.invoke('generate-product-image', {
        body: {
          prompt: aiPrompt.trim() || undefined,
          productTitle: productTitle.trim() || undefined,
          existingImages: images,
          style: aiStyle
        },
      });

      if (error) {
        console.error('Supabase function error:', error);
        throw error;
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to generate image');
      }

      console.log('AI image generated successfully:', data.imageUrl);

      // Add the generated image to the gallery
      const updatedImages = [...images, data.imageUrl];
      onImagesChange(updatedImages);

      setIsAiDialogOpen(false);
      setAiPrompt('');
      toast.success('תמונה נוצרה בהצלחה באמצעות AI');

    } catch (error: any) {
      console.error('Error generating AI image:', error);
      toast.error(`שגיאה ביצירת תמונה: ${error.message || 'שגיאה לא ידועה'}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePreviewImage = (imageUrl: string) => {
    setPreviewImage(imageUrl);
  };

  const downloadImage = async (imageUrl: string, filename: string) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('שגיאה בהורדת התמונה');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-base font-medium">גלרית תמונות המוצר</Label>
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileUpload}
            className="hidden"
          />
          <Button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            variant="outline"
          >
            <Upload className="w-4 h-4 mr-2" />
            העלה תמונות
          </Button>
        </div>
      </div>

      {/* Image gallery with drag and drop */}
      {images.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={images} strategy={verticalListSortingStrategy}>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((imageUrl, index) => (
                <SortableImageItem
                  key={imageUrl}
                  imageUrl={imageUrl}
                  index={index}
                  onRemove={() => handleRemoveImage(index)}
                  onPreview={() => handlePreviewImage(imageUrl)}
                  onDownload={() => downloadImage(imageUrl, `product-image-${index + 1}.jpg`)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <Card className="border-dashed border-2 border-muted-foreground/25">
          <CardContent className="flex flex-col items-center justify-center py-8">
            <ImagePlus className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">
              אין תמונות עדיין<br />
              העלה תמונות או צור אותן באמצעות AI<br />
              <span className="text-sm text-muted-foreground">
                גרור ושחרר תמונות כדי לשנות את הסדר. התמונה הראשונה תהיה התמונה הראשית.
              </span>
            </p>
          </CardContent>
        </Card>
      )}

      {/* Preview Dialog */}
      <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>תצוגה מקדימה</DialogTitle>
          </DialogHeader>
          {previewImage && (
            <div className="flex justify-center">
              <img
                src={previewImage}
                alt="תצוגה מקדימה"
                className="max-w-full max-h-[70vh] object-contain rounded-md"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductImageGallery;